import React, { useState } from 'react';
import { loucaApi } from '../../services/loucaApi';

/**
 * Formulaire pour créer une nouvelle inspection
 */
const NewInspectionForm = ({ config, onInspectionCreated }) => {
  const [videoFile, setVideoFile] = useState(null);
  const [chunkIndex, setChunkIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoFile(file);
      setError(null);
    }
  };

  const handleCreateSession = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await loucaApi.createSessionV3(config);
      
      if (response.status === 'success' && response.session_id) {
        setSessionId(response.session_id);
        return response.session_id;
      } else {
        throw new Error(response.message || 'Erreur lors de la création de la session');
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleUploadChunk = async (sessionId, file, index) => {
    try {
      setLoading(true);
      setError(null);
      setUploadProgress(0);

      // Simulate upload progress (since we can't track real progress with axios easily)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await loucaApi.uploadChunkV3(sessionId, file, index);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.status === 'success' || response.status === 'processing') {
        return response;
      } else {
        throw new Error(response.message || 'Erreur lors de l\'upload');
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!videoFile) {
      setError('Veuillez sélectionner une vidéo');
      return;
    }

    try {
      // Step 1: Create session
      const newSessionId = await handleCreateSession();
      
      // Step 2: Upload chunk
      await handleUploadChunk(newSessionId, videoFile, chunkIndex);
      
      // Step 3: Notify parent
      if (onInspectionCreated) {
        onInspectionCreated(newSessionId);
      }
      
      // Reset form
      setVideoFile(null);
      setSessionId(null);
      setChunkIndex(0);
    } catch (err) {
      // Error already set in handlers
      console.error('Error creating inspection:', err);
    }
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white">Nouvelle Inspection</h2>
        <p className="text-sm text-slate-400">Créez une nouvelle session et uploadez une vidéo</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <label className="text-white font-medium">Fichier vidéo</label>
          <input
            type="file"
            accept="video/*"
            onChange={handleFileChange}
            className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-500 file:text-white hover:file:bg-orange-600"
          />
          {videoFile && (
            <p className="text-sm text-slate-400">
              {videoFile.name} ({(videoFile.size / 1024 / 1024).toFixed(2)} MB)
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-white font-medium">Index du chunk</label>
          <input
            type="number"
            value={chunkIndex}
            onChange={(e) => setChunkIndex(parseInt(e.target.value) || 0)}
            min="0"
            className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <p className="text-xs text-slate-400">
            Index du chunk vidéo (0 pour la première vidéo)
          </p>
        </div>

        {uploadProgress > 0 && uploadProgress < 100 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm text-slate-400">
              <span>Upload en cours...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div
                className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !videoFile}
          className="w-full px-6 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              {sessionId ? 'Upload en cours...' : 'Création de la session...'}
            </span>
          ) : (
            'Créer l\'inspection'
          )}
        </button>
      </form>

      {sessionId && (
        <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
          <p className="text-green-400 text-sm">
            ✅ Session créée: <span className="font-mono">{sessionId}</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default NewInspectionForm;



